import {
    Component,
    computed,
    effect,
    input,
    signal,
    viewChild,
    forwardRef,
    Signal,
    ElementRef,
    InputSignal,
    WritableSignal
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";

import { QuillModules, QuillEditorComponent } from "ngx-quill";

import { OnChangeFn, OnTouchedFn, OnChangeFnMutation, } from "../../models";
import { RichTextEditorToolbar } from "@controls/text-editors/rich-text-editor/models";
import { RichTextEditorChangeService } from "../services/rich-text-editor-change.service";
import { RichTextEditorToolbarService } from "../services/rich-text-editor-toolbar.service";


@Component({
    selector: "rich-text-editor",
    templateUrl: "./rich-text-editor.component.html",
    styleUrls: ["./rich-text-editor.component.scss"],
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => RichTextEditorComponent),
        multi: true
    }]
})
export class RichTextEditorComponent implements ControlValueAccessor {
    // View child statements.
    public editor: Signal<QuillEditorComponent> = viewChild(QuillEditorComponent);

    // Input properties.
    public placeholder: InputSignal<string> = input("");
    public modules: InputSignal<QuillModules> = input({});

    // Computed properties.
    protected toolbar: Signal<RichTextEditorToolbar> = this.computeToolbar();

    // Writable signals.
    private onTouched: WritableSignal<OnTouchedFn> = signal(null);
    private onChange: WritableSignal<OnChangeFn> = signal(null);

    protected bounds: WritableSignal<HTMLElement> = signal(null);
    protected disabled: WritableSignal<boolean> = signal(false);
    protected value: WritableSignal<string> = signal("");

    constructor(private toolbarService: RichTextEditorToolbarService,
                private changeService: RichTextEditorChangeService,
                private elementRef: ElementRef) {
        this.configureEditor();
    }

    private computeToolbar(): Signal<RichTextEditorToolbar> {
        return computed((): RichTextEditorToolbar => {
            // Get modules object value. Return null if it is unset.
            const modules: QuillModules = this.modules();

            if (!modules) return null;

            // Ensure default toolbar is shown if no toolbar was specified.
            modules.toolbar ??= this.toolbarService.getDefaultToolbar();

            return modules.toolbar;
        });
    }

    private configureEditor(): void {
        // Set the bounds of quill editor to this component native element.
        this.bounds.set(this.elementRef.nativeElement);

        // React to editor apparition or on-touched value change, to update the latter in the editor.
        effect((): void => {
            const editor: QuillEditorComponent = this.editor();
            if (!editor) return;

            const onTouched: OnTouchedFn = this.onTouched();
            editor.registerOnTouched((): void => onTouched?.());
        });

        // React to editor apparition or on-change value change, to update the latter in the editor.
        effect((): void => {
            const editor: QuillEditorComponent = this.editor();
            if (!editor) return;

            const onChange: OnChangeFn = this.onChange();

            // Set of mutations to apply to original on-change function.
            const mutations: OnChangeFnMutation[] = [this.changeService.makeNewlinesAware()];

            // Register on-change function with the mutations applied.
            editor.registerOnChange(this.changeService.getChange(onChange, mutations));
        });
    }

    public registerOnChange(fn: OnChangeFn): void {
        this.onChange.set(fn);
    }

    public registerOnTouched(fn: OnTouchedFn): void {
        this.onTouched.set(fn);
    }

    public writeValue(value: string): void {
        this.value.set(value);
    }

    public setDisabledState(disabled: boolean): void {
        this.disabled.set(disabled);
    }
}